import { useState } from 'react'
import { Info, Plus } from 'lucide-react'
import { EmptyState, ErrorState, FormDialog, PageHeader } from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { useLookup } from '@/hooks/useLookup'
import { toApiProblem } from '@/lib/api/client'
import {
  useFolders, useLibraryMutations, useRetentionReview, useRetentionRules,
} from '../api'

/** Folders and who can open them; retention rules and what they do not do. */
export function LibrarySettingsPage() {
  useBreadcrumb()
  const folders = useFolders()
  const rules = useRetentionRules()
  const review = useRetentionReview()
  const roles = useLookup<{ id: string; code: string; name: string }>('roles')
  const entities = useLookup<{ id: string; name: string }>('legalEntities')
  const { saveFolder, saveRetentionRule } = useLibraryMutations()

  const [folderDraft, setFolderDraft] = useState<{
    name: string; description: string; accessMode: string; roleIds: string[]
  } | null>(null)
  const [ruleDraft, setRuleDraft] = useState<{
    code: string; name: string; retainMonths: string; basis: string; statutoryReference: string
  } | null>(null)
  const [error, setError] = useState<string | null>(null)

  return (
    <div className="space-y-4">
      <PageHeader
        title="Library setup"
        subtitle="Folders and who can open them; how long things are kept."
        actions={
          <div className="flex gap-2">
            <Button
              type="button"
              onClick={() => {
                setError(null)
                setFolderDraft({ name: '', description: '', accessMode: '', roleIds: [] })
              }}
            >
              <Plus className="size-4" aria-hidden="true" />
              New folder
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setError(null)
                setRuleDraft({
                  code: '', name: '', retainMonths: '', basis: 'PUBLICATION',
                  statutoryReference: '',
                })
              }}
            >
              New retention rule
            </Button>
          </div>
        }
      />

      <Card>
        <h2 className="border-b border-slate-100 p-4 font-semibold text-[var(--color-heading)]">Folders</h2>
        {folders.isPending && <Skeleton className="m-4 h-20" />}
        {folders.isError && <ErrorState error={folders.error} onRetry={() => void folders.refetch()} />}
        {folders.isSuccess && folders.data.length === 0 && (
          <EmptyState
            title="No folder exists"
            description="A folder is where library entries live and what decides who can open them. Who may see one is chosen when it is created — there is no default."
          />
        )}
        {folders.isSuccess && folders.data.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 text-slate-600">
                <tr>
                  <th className="p-3 text-start font-medium">Folder</th>
                  <th className="p-3 text-start font-medium">Who can open it</th>
                  <th className="p-3 text-end font-medium">Entries</th>
                </tr>
              </thead>
              <tbody>
                {folders.data.map((folder) => (
                  <tr key={folder.id} className="border-b border-slate-100 align-top">
                    <td className="p-3">{folder.name}</td>
                    <td className="p-3 max-w-md text-xs text-slate-600">{folder.accessNote}</td>
                    <td className="p-3 text-end">{folder.documentCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card>
        <h2 className="border-b border-slate-100 p-4 font-semibold text-[var(--color-heading)]">
          Retention rules
        </h2>
        <p className="flex items-start gap-2 border-b border-slate-100 p-4 text-sm text-slate-700">
          <Info className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
          Nothing is ever deleted automatically. A rule produces a review list, because a
          system that destroyed employment records on a timer would be the one feature whose
          misfire nobody notices until the records are needed.
        </p>
        {rules.isPending && <Skeleton className="m-4 h-16" />}
        {rules.isSuccess && rules.data.length === 0 && (
          <EmptyState
            title="No retention rule is defined"
            description="Nothing ships: retention periods come from law and from your own policy, and a number invented here would be quoted back as advice."
          />
        )}
        {rules.isSuccess && rules.data.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 text-slate-600">
                <tr>
                  <th className="p-3 text-start font-medium">Code</th>
                  <th className="p-3 text-start font-medium">Rule</th>
                  <th className="p-3 text-start font-medium">What it means</th>
                  <th className="p-3 text-start font-medium">Reference</th>
                </tr>
              </thead>
              <tbody>
                {rules.data.map((rule) => (
                  <tr key={rule.id} className="border-b border-slate-100 align-top">
                    <td className="p-3 font-mono">{rule.code}</td>
                    <td className="p-3">{rule.name}</td>
                    <td className="p-3 max-w-md text-xs text-slate-600">{rule.note}</td>
                    <td className="p-3 text-xs text-slate-600">
                      {rule.statutoryReference ?? 'None cited'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card>
        <h2 className="border-b border-slate-100 p-4 font-semibold text-[var(--color-heading)]">
          Retention review
        </h2>
        {review.isPending && <Skeleton className="m-4 h-16" />}
        {review.isSuccess && review.data.length === 0 && (
          <EmptyState
            title="Nothing is in the library yet"
            description="Once entries exist, this lists what is past its retention period — and what has no rule at all."
          />
        )}
        {review.isSuccess && review.data.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 text-slate-600">
                <tr>
                  <th className="p-3 text-start font-medium">Entry</th>
                  <th className="p-3 text-start font-medium">Keep until</th>
                  <th className="p-3 text-start font-medium">What the rule says</th>
                </tr>
              </thead>
              <tbody>
                {review.data.map((row) => (
                  <tr key={row.documentId} className="border-b border-slate-100 align-top">
                    <td className="p-3">{row.title}</td>
                    <td className={`p-3 ${row.dueForReview ? 'font-medium text-amber-700' : ''}`}>
                      {row.keepUntil ?? '—'}
                    </td>
                    <td className="p-3 max-w-lg text-xs text-slate-600">{row.note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <FormDialog
        open={folderDraft != null}
        onOpenChange={(open) => { if (!open) { setFolderDraft(null); setError(null) } }}
        title="New folder"
        description="Who may open this is chosen now. There is no default, and a restricted folder with no roles is refused."
        submitLabel="Create folder"
        loading={saveFolder.isPending}
        error={error}
        onSubmit={() => {
          if (!folderDraft) return
          void (async () => {
            setError(null)
            try {
              await saveFolder.mutateAsync({
                body: {
                  legalEntityId: entities.data?.[0]?.id ?? null,
                  name: folderDraft.name,
                  description: folderDraft.description || null,
                  accessMode: folderDraft.accessMode,
                  access: folderDraft.roleIds.map((roleId) => ({ roleId, canWrite: true })),
                },
              })
              setFolderDraft(null)
            } catch (caught) {
              setError(toApiProblem(caught)?.detail ?? 'That folder could not be created')
            }
          })()
        }}
      >
        {folderDraft && (
          <div className="space-y-3">
            <div>
              <Label htmlFor="folder-name">Name</Label>
              <Input
                id="folder-name" value={folderDraft.name}
                onChange={(event) => setFolderDraft({ ...folderDraft, name: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="folder-access">Who can open it</Label>
              <Select
                id="folder-access"
                className="w-full"
                value={folderDraft.accessMode}
                onChange={(event) =>
                  setFolderDraft({ ...folderDraft, accessMode: event.target.value })}
              >
                <option value="">Choose one…</option>
                <option value="EVERYONE">Anybody who can read documents</option>
                <option value="ROLES">Only the roles I name</option>
              </Select>
              <p className="mt-1 text-xs text-slate-600">
                No default: who may read a folder of salary policies is not a question this
                system can answer for you.
              </p>
            </div>
            {folderDraft.accessMode === 'ROLES' && (
              <fieldset className="space-y-2">
                <legend className="text-sm font-medium">Roles that can open it</legend>
                {roles.data?.map((role) => (
                  <label key={role.id} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      className="size-4"
                      value={role.id}
                      checked={folderDraft.roleIds.includes(role.id)}
                      onChange={(event) => setFolderDraft({
                        ...folderDraft,
                        roleIds: event.target.checked
                          ? [...folderDraft.roleIds, role.id]
                          : folderDraft.roleIds.filter((id) => id !== role.id),
                      })}
                    />
                    {role.name ?? role.code}
                  </label>
                ))}
                <p className="text-xs text-slate-600">
                  At least one, or it is a folder nobody — including you — can open.
                </p>
              </fieldset>
            )}
          </div>
        )}
      </FormDialog>

      <FormDialog
        open={ruleDraft != null}
        onOpenChange={(open) => { if (!open) { setRuleDraft(null); setError(null) } }}
        title="New retention rule"
        description="This produces a review list. It never deletes anything."
        submitLabel="Save rule"
        loading={saveRetentionRule.isPending}
        error={error}
        onSubmit={() => {
          if (!ruleDraft) return
          void (async () => {
            setError(null)
            try {
              await saveRetentionRule.mutateAsync({
                legalEntityId: entities.data?.[0]?.id ?? null,
                code: ruleDraft.code,
                name: ruleDraft.name,
                retainMonths: ruleDraft.retainMonths ? Number(ruleDraft.retainMonths) : null,
                basis: ruleDraft.basis,
                statutoryReference: ruleDraft.statutoryReference || null,
              })
              setRuleDraft(null)
            } catch (caught) {
              setError(toApiProblem(caught)?.detail ?? 'That rule could not be saved')
            }
          })()
        }}
      >
        {ruleDraft && (
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="rule-code">Code</Label>
              <Input
                id="rule-code" value={ruleDraft.code}
                onChange={(event) => setRuleDraft({ ...ruleDraft, code: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="rule-name">Name</Label>
              <Input
                id="rule-name" value={ruleDraft.name}
                onChange={(event) => setRuleDraft({ ...ruleDraft, name: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="rule-months">Keep for, months</Label>
              <Input
                id="rule-months" type="number" value={ruleDraft.retainMonths}
                onChange={(event) =>
                  setRuleDraft({ ...ruleDraft, retainMonths: event.target.value })}
              />
              <p className="mt-1 text-xs text-slate-600">
                No default: retention periods come from law and from your own policy.
              </p>
            </div>
            <div>
              <Label htmlFor="rule-basis">Counting from</Label>
              <Select
                id="rule-basis"
                className="w-full"
                value={ruleDraft.basis}
                onChange={(event) => setRuleDraft({ ...ruleDraft, basis: event.target.value })}
              >
                <option value="PUBLICATION">Publication</option>
                <option value="SUPERSEDED">Being superseded</option>
              </Select>
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="rule-reference">What it comes from</Label>
              <Input
                id="rule-reference" value={ruleDraft.statutoryReference}
                onChange={(event) =>
                  setRuleDraft({ ...ruleDraft, statutoryReference: event.target.value })}
              />
              <p className="mt-1 text-xs text-slate-600">
                Cite the law or the internal policy. This system cites none of its own.
              </p>
            </div>
          </div>
        )}
      </FormDialog>
    </div>
  )
}
